import React, { useState, useEffect } from 'react';
import { 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { MatrixGraduationCap } from './MatrixEmojis';

const AuthWrapper = ({ children, requireAuth = true, darkMode = false }) => {
  const [user, setUser] = useState(undefined); // undefined = loading, null = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState('');
  const [error, setError] = useState('');  // Listen for auth state changes
  useEffect(() => {
    console.log('🔐 Setting up auth state listener...');
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('🔐 Auth state changed:', currentUser ? 'User authenticated' : 'User not authenticated');
      
      if (currentUser) {
        console.log('🔐 User details:', { email: currentUser.email, uid: currentUser.uid });
        setUser(currentUser);
        await createOrUpdateUserProfile(currentUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      console.log('🔐 Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);
  // Handle redirect result on component mount
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        console.log('🔐 Checking for redirect result...');
        const result = await getRedirectResult(auth);
        if (result) {
          console.log('🔐 Redirect authentication successful:', result.user);
          setAuthLoading('');
          setError('');
        } else {
          console.log('🔐 No redirect result found');
        }
      } catch (error) {
        console.error('🔐 Redirect authentication error:', error);
        setError(`Authentication failed: ${error.message}`);
        setAuthLoading('');
      }
    };

    handleRedirectResult();
  }, []);

  const createOrUpdateUserProfile = async (currentUser) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        // Create user profile if doesn't exist
        const newProfile = {
          uid: currentUser.uid,
          email: currentUser.email,
          name: currentUser.displayName,
          photoURL: currentUser.photoURL,
          createdAt: serverTimestamp(),
          profileComplete: false,
          provider: currentUser.providerData[0]?.providerId || 'unknown'
        };
        await setDoc(doc(db, 'users', currentUser.uid), newProfile);
      }
    } catch (error) {
      console.error('Error creating user profile:', error);
    }
  };  const handleGoogleLogin = async () => {
    setAuthLoading('google');
    setError('');
    
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      console.log('Starting Google authentication via popup...');
      
      // Try popup first (better user experience)
      try {
        const result = await signInWithPopup(auth, provider);
        console.log('Popup authentication successful:', result.user);
        setAuthLoading('');
      } catch (popupError) {
        console.warn('Popup blocked or failed, falling back to redirect:', popupError);
        
        // If popup fails (e.g., popup blocked), fall back to redirect
        if (popupError.code === 'auth/popup-blocked' || 
            popupError.code === 'auth/popup-closed-by-user' ||
            popupError.code === 'auth/cancelled-popup-request') {
          
          console.log('Falling back to redirect authentication...');
          await signInWithRedirect(auth, provider);
        } else {
          throw popupError;
        }
      }
      // Note: User will be redirected away, so we don't clear loading here
    } catch (error) {
      console.error('Google login error:', error);
      setError(`Google login failed: ${error.message}`);
      setAuthLoading('');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      setError(`Logout failed: ${error.message}`);
    }  };

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto ${darkMode ? 'border-indigo-400' : 'border-indigo-600'}`}></div>
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading...</p>
        </div>
      </div>
    );
  }  // Show login screen if authentication is required but user is not logged in
  if (requireAuth && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center px-4 py-8">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        <div className="relative max-w-md w-full">
          {/* Main login card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 shadow-2xl">
            {/* Header with icon */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-4 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                </svg>
              </div>
              
              <h1 className="text-3xl font-bold text-white mb-2">
                Welcome to MBBS Portal
              </h1>
              <p className="text-blue-100 text-lg">
                Sign in to access your application portal and manage your MBBS admissions
              </p>
            </div>
            
            {/* Error message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-400/30 rounded-xl backdrop-blur-sm">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-red-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-red-300 font-medium">Authentication Error</p>
                    <p className="text-black text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleLogin}
              disabled={authLoading === 'google'}
              className="w-full group relative overflow-hidden bg-white hover:bg-gray-50 text-gray-900 font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <div className="flex items-center justify-center space-x-3">
                {authLoading === 'google' ? (
                  <>
                    <svg className="animate-spin w-5 h-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>Continue with Google</span>
                  </>
                )}
              </div>
              
              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
            
            {/* Benefits section */}
            <div className="mt-8 p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="font-semibold text-white">Why Sign In?</h4>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full"></div>
                  <span className="text-blue-100 text-sm">Secure application submission</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full"></div>
                  <span className="text-blue-100 text-sm">Real-time status tracking</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-pink-400 to-blue-500 rounded-full"></div>
                  <span className="text-blue-100 text-sm">No need to remember application IDs</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-green-400 to-purple-500 rounded-full"></div>
                  <span className="text-blue-100 text-sm">Access to all your applications</span>
                </div>
              </div>
            </div>
              {/* Footer note */}
            <div className="mt-6 text-center">
              <p className="text-blue-200 text-xs flex items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <circle cx="12" cy="16" r="1"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Secure authentication powered by Google OAuth 2.0
              </p>
              <p className="text-blue-300 text-xs mt-1 flex items-center justify-center gap-2">
                Login opens in a popup - no page redirects needed
              </p>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full opacity-70 animate-bounce"></div>
          <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-gradient-to-r from-pink-400 to-blue-500 rounded-full opacity-70 animate-bounce delay-1000"></div>
        </div>
      </div>
    );
  }

  // Render children with user context if authenticated or auth not required
  return React.cloneElement(children, { user, onLogout: handleLogout });
};

export default AuthWrapper;
